/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/scandipwa
 */

import { isSignedIn as isSigned } from '@scandipwa/scandipwa/src/util/Auth/Token';
import { ReactComponent as ShoppingBagIcon } from 'assets/icons/shopping-bag.svg';
import { lazy, Suspense } from 'react';

import CartIcon from 'Component/CartIcon';
import ClickOutside from 'Component/ClickOutside';
import CurrencySwitcher from 'Component/CurrencySwitcher';
import {
    CART,
    CART_EDITING,
    CART_OVERLAY,
    CATEGORY,
    CHECKOUT,
    CHECKOUT_ACCOUNT,
    CHECKOUT_SUCCESS,
    CMS_PAGE,
    CONTACT_US,
    CURRENCY_PLN,
    CUSTOMER_ACCOUNT,
    CUSTOMER_ACCOUNT_PAGE,
    CUSTOMER_SUB_ACCOUNT,
    CUSTOMER_WISHLIST,
    FILTER,
    LANG_CODE_PL,
    MENU,
    MENU_SUBCATEGORY,
    NO_MATCH,
    PDP,
    POPUP,
    PRODUCT_COMPARE,
    SEARCH
} from 'Component/Header/Header.config';
import Link from 'Component/Link';
import { DEFAULT_STATE_NAME } from 'Component/NavigationAbstract/NavigationAbstract.config';
import OfflineNotice from 'Component/OfflineNotice';
import StoreSwitcher from 'Component/StoreSwitcher';
import UserIcon from 'Component/UserIcon';
import { Header as SourceHeader } from 'SourceComponent/Header/Header.component';
import { isCrawler, isSSR } from 'Util/Browser';

import PageWarnings from '../PageWarnings';
import { SPACER } from './Header.config';

import './Header.style';

export const CartOverlay = lazy(() => import('Component/CartOverlay'));
export const MyAccountOverlay = lazy(() => import('Component/MyAccountOverlay'));

/** @namespace Pwa/Component/Header/Component */
export class Header extends SourceHeader {
    renderMap = {
        cancel: this.renderCancelButton.bind(this),
        back: this.renderBackButton.bind(this),
        close: this.renderCloseButton.bind(this),
        title: this.renderTitle.bind(this),
        logo: this.renderLogo.bind(this),
        search: this.renderSearchField.bind(this),
        switch: this.switchLanguage.bind(this),
        offer: this.renderOfferButton.bind(this),
        minicart: this.renderMinicart.bind(this),
        account: this.renderAccount.bind(this)
    };

    stateMap = {
        [DEFAULT_STATE_NAME]: {
            title: true,
            logo: true,
            offer: true
        },
        [NO_MATCH]: {
            title: true
        },
        [POPUP]: {
            title: true,
            close: true
        },
        [PDP]: {
            back: true,
            title: false,
            logo: true
        },
        [CATEGORY]: {
            back: true,
            title: true,
            logo: false
        },
        [CUSTOMER_ACCOUNT]: {
            logo: true
        },
        [CUSTOMER_SUB_ACCOUNT]: {
            logo: true,
            back: true
        },
        [CUSTOMER_ACCOUNT_PAGE]: {
            logo: true
        },
        [CUSTOMER_WISHLIST]: {
            share: true,
            logo: true,
            ok: true
        },
        [MENU]: {
            search: true
        },
        [MENU_SUBCATEGORY]: {
            back: true,
            title: true,
            search: true
        },
        [SEARCH]: {
            search: true
        },
        [CART]: {
            title: false,
            logo: true
        },
        [CART_OVERLAY]: {
            title: true
        },
        [CART_EDITING]: {
            ok: true,
            title: true,
            cancel: true
        },
        [FILTER]: {
            close: true,
            title: true
        },
        [CHECKOUT]: {
            back: true,
            title: true,
            account: true
        },
        [CHECKOUT_SUCCESS]: {
            title: true,
            account: true
        },
        [CHECKOUT_ACCOUNT]: {
            title: true,
            close: true
        },
        [CMS_PAGE]: {
            back: true,
            title: true
        },
        [CONTACT_US]: {
            title: true,
            back: true
        },
        [PRODUCT_COMPARE]: {
            title: true,
            back: true
        }
    };

    switchLanguage() {
        const {
            defaultCustomerCurrency,
            current_currency_code,
            device: { isMobile },
            currencyData: { available_currencies_data = [] } = {}
        } = this.props;

        const isLoggedIn = isSigned();
        const { pathname } = location;
        const regex = /\/[a-z]{2}\//g;
        const langCode = pathname.match(regex)?.[0] || '';
        const currencyList = !isLoggedIn && langCode.includes(LANG_CODE_PL)
            ? available_currencies_data.filter((currency) => currency?.id === CURRENCY_PLN)
            : available_currencies_data;

        return (
            <div block="Header" elem="Switcher" key="switch-language">
                { !isLoggedIn && (defaultCustomerCurrency || current_currency_code)
                    && currencyList?.length <= 1 && <h4>{ defaultCustomerCurrency || current_currency_code }</h4> }
                { isLoggedIn && (defaultCustomerCurrency || current_currency_code)
                    && <h4>{ defaultCustomerCurrency || current_currency_code }</h4> }
                { !isMobile && !isLoggedIn && currencyList?.length > 1 && <CurrencySwitcher /> }

                <StoreSwitcher />
            </div>
        );
    }

    renderWelcomeMessage() {
        const { firstname = '', isSignedIn } = this.props;

        return isSignedIn ? (
            <div block="Header" elem="Welcome" mods={ { signedIn: true } }>
                <span block="Header" elem="WelcomeHeading">
                    { __('Welcome %s', firstname) }
                </span>
                <span block="Header" elem="WelcomeSubheading">
                    { __('client zone') }
                </span>
            </div>
        ) : (
            <div block="Header" elem="Welcome" mods={ { signedIn: true } }>
                <span block="Header" elem="WelcomeHeading">
                    { __('Sign in') }
                </span>
            </div>
        );
    }

    renderAccountButton() {
        const { onMyAccountButtonClick, device, isSignedIn } = this.props;

        if (device.isMobile) {
            return null;
        }

        return (
            <button
              block="Header"
              elem="MyAccountWrapper"
              mods={ { signedIn: isSignedIn } }
              tabIndex="0"
              onClick={ onMyAccountButtonClick }
              aria-label="Open my account"
              id="myAccount"
              key="myAccountButton"
            >
                <UserIcon />
                { this.renderWelcomeMessage() }
            </button>
        );
    }

    renderAccount(isVisible = false) {
        const {
            onMyAccountOutsideClick,
            isCheckout,
            device: { isMobile }
        } = this.props;

        // on mobile hide button if not in checkout
        if (isMobile && !isCheckout) {
            return null;
        }

        return (
            <div key="account" block="Header" elem="MyAccountContainer">
                <ClickOutside onClick={ onMyAccountOutsideClick }>
                    <div
                      aria-label="My account"
                      block="Header"
                      elem="MyAccount"
                    >
                        { this.renderAccountButton(isVisible) }
                        { this.renderAccountOverlay() }
                    </div>
                </ClickOutside>
            </div>
        );
    }

    renderMinicartItemsQty() {
        const { cartTotals: { items } } = this.props;

        if (items?.length === 0) {
            return null;
        }

        return (
            <span
              aria-label="Items in cart"
              block="Header"
              elem="MinicartItemCount"
            >
                { items?.length }
            </span>
        );
    }

    renderOfferItemsQty() {
        const { offerItemsCount } = this.props; // to change after got proper query

        if (!offerItemsCount) {
            return null;
        }

        return (
            <span
              aria-label="Items in cart"
              block="Header"
              elem="OfferItemCount"
            >
                { offerItemsCount }
            </span>
        );
    }

    renderMinicartButton() {
        return (
            <div
              block="Header"
              elem="MinicartButtonWrapper"
              aria-label={ __('Cart') }
              key="minicartButton"
            >
                <CartIcon />
                { this.renderMinicartItemsQty() }
            </div>
        );
    }

    renderOfferButton() {
        const {
            activeOfferId,
            device: { isMobile },
            isSignedIn
        } = this.props;

        if (isMobile || !isSignedIn) {
            return null;
        }

        const link = activeOfferId ? `/offers/${activeOfferId}` : '/my-account/my-offers';

        return (
            <Link to={ link } key="offer">
                <div
                  block="Header"
                  elem="OfferButtonWrapper"
                  aria-label={ __('Offer') }
                  key="OffercartButton"
                >
                    <ShoppingBagIcon />
                    { this.renderOfferItemsQty() }
                </div>
            </Link>
        );
    }

    renderMinicart(isVisible = false) {
        const {
            device: { isMobile }
        } = this.props;

        if (isMobile) {
            return null;
        }

        return (
            <Link to="/cart" key="minicart">
                <div
                  block="Header"
                  elem="Button"
                  mods={ { isVisible, type: 'minicart' } }
                >
                    { this.renderMinicartButton() }
                </div>
            </Link>
        );
    }

    renderAccountOverlay() {
        const {
            isCheckout,
            showMyAccountLogin,
            onSignIn
        } = this.props;

        // This is here to prevent the popup-suspense from rendering
        if (!showMyAccountLogin) {
            return null;
        }

        return (
            <Suspense fallback={ this.renderAccountOverlayFallback() }>
                <MyAccountOverlay
                  onSignIn={ onSignIn }
                  isCheckout={ isCheckout }
                />
            </Suspense>
        );
    }

    renderTitle(isVisible = false) {
        const { navigationState: { title } } = this.props;

        return (
            <p
              key="title"
              block="Header"
              elem="Title"
              mods={ { isVisible } }
            >
                { title }
            </p>
        );
    }

    render() {
        const { stateMap } = this;
        const {
            navigationState: { name, isHiddenOnMobile = false },
            isCheckout,
            device: { isMobile }
        } = this.props;

        if (!isMobile) {
            // hide edit button on desktop
            stateMap[CUSTOMER_WISHLIST].edit = false;
            stateMap[CUSTOMER_WISHLIST].share = false;
            stateMap[CART_OVERLAY].edit = false;
        }

        const headerHeight = document.querySelector('.Header')?.offsetHeight;

        return (
            <section
              block="Header"
              elem="Wrapper"
              // eslint-disable-next-line no-magic-numbers
              style={ { marginBottom: headerHeight ? `${headerHeight + SPACER}px` : 0 } }
              mods={ { isPrerendered: isSSR() || isCrawler() } }
            >
                <header
                  block="Header"
                  mods={ { name, isHiddenOnMobile, isCheckout } }
                  mix={ { block: 'FixedElement', elem: 'Top' } }
                  ref={ this.logoRef }
                >
                    <PageWarnings />
                    <nav block="Header" elem="Nav">
                        <div block="Header" elem="NavInner">
                            { this.renderNavigationState() }
                        </div>
                    </nav>

                    { this.renderMenu() }
                </header>
                <OfflineNotice />
            </section>
        );
    }
}

export default Header;
